import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Wrench, Phone, Building2, History } from "lucide-react";

const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
};

const TYPES = {
  visit: { label: "Servicebesuch", dot: "bg-primary", icon: Wrench },
  call: { label: "Gespräch", dot: "bg-info", icon: Phone },
  created: { label: "Angelegt", dot: "bg-success", icon: Building2 },
};

export function CustomerTimeline({ customer, reports = [], conversationEntries = [] }) {
  const items = [];

  reports.forEach((r) => {
    items.push({
      key: `report-${r.id}`,
      type: "visit",
      date: r.service_date || r.created_at,
      title: r.title || "Servicebericht",
      subtitle: [r.technician_name, ((r.work_hours || 0) + (r.travel_hours || 0)) ? `${(r.work_hours || 0) + (r.travel_hours || 0)}h` : null].filter(Boolean).join(" · "),
      to: `/service-reports/${r.id}/edit`,
    });
  });

  conversationEntries.forEach((e, i) => {
    const text = stripHtml(e.text || e.note || e.content || e.notes || "");
    items.push({
      key: `conv-${e.id || i}`,
      type: "call",
      date: e.datetime_start || e.created_at || e.datetime,
      title: e.title || "Gesprächsnotiz",
      subtitle: text,
    });
  });

  if (customer?.created_at) {
    items.push({ key: "created", type: "created", date: customer.created_at, title: "Kunde angelegt", subtitle: customer.company_name });
  }

  const sorted = items
    .filter((it) => it.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <Card data-testid="customer-timeline">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><History className="w-5 h-5 text-primary" />Timeline</CardTitle>
        <CardDescription>Alle Besuche und Aktivitäten chronologisch</CardDescription>
      </CardHeader>
      <CardContent>
        {sorted.length > 0 ? (
          <ol className="relative border-l border-border ml-3 space-y-5">
            {sorted.map((it) => {
              const meta = TYPES[it.type];
              const Icon = meta.icon;
              const Inner = (
                <div className="rounded-lg border border-border bg-card p-3 hover:bg-accent/40 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm">{it.title}</p>
                    <time className="text-xs text-muted-foreground shrink-0">{fmt(it.date)}</time>
                  </div>
                  {it.subtitle && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{it.subtitle}</p>}
                  <Badge variant="secondary" className="mt-2 text-[10px] uppercase tracking-wide">{meta.label}</Badge>
                </div>
              );
              return (
                <li key={it.key} className="ml-6" data-testid={`timeline-item-${it.type}`}>
                  <span className={`absolute -left-[13px] w-6 h-6 rounded-full ring-4 ring-background flex items-center justify-center ${meta.dot}`}>
                    <Icon className="w-3 h-3 text-white" />
                  </span>
                  {it.to ? <Link to={it.to} className="block">{Inner}</Link> : Inner}
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="empty-state py-10" data-testid="timeline-empty">
            <History className="w-12 h-12 text-muted-foreground/40" />
            <p className="text-muted-foreground mt-3">Noch keine Aktivitäten erfasst</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
